// src/components/Scenarios.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../components/PromptInput';
import type { PromptPayload } from './CreatePage';

type Frame = {
  title: string;
  top?: string;
  bottom?: string;
  pose?: string;
  set?: string;
  extra?: string;
};

type DiffsonaScenario = {
  key: string;
  title: string;
  description?: string;
  coverEmoji?: string;
  frames: Frame[];
};

@customElement('diffsona-scenarios')
export class DiffsonaScenarios extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) slug = '';

  @state() private selected?: DiffsonaScenario;
  @state() private editingIndex: number | null = null;

  // Starter packs — you can extend/replace these dynamically later
  private scenarios: DiffsonaScenario[] = [
    {
      key: 'morning-routine',
      title: 'Morning Routine',
      coverEmoji: '🌅',
      description: 'Wake up → get ready → out the door.',
      frames: [
        { title: 'Wake up', pose: 'stretching in bed', set: 'soft morning light, bedroom', top: 'lounge tank', bottom: 'cotton shorts' },
        { title: 'Getting dressed', pose: 'standing by closet', set: 'bedroom, wardrobe in background', top: 'tailored blouse', bottom: 'high-rise trousers' },
        { title: 'Makeup', pose: 'seated at vanity, looking in mirror', set: 'vanity lights', extra: 'soft glam' },
        { title: 'Out the door', pose: 'walking, mid-stride', set: 'apartment hallway', top: 'trench coat', bottom: 'slim jeans' },
      ],
    },
    {
      key: 'street-style',
      title: 'Street Style Day',
      coverEmoji: '🚶‍♀️',
      description: 'Coffee run → crosswalk → storefront → alley editorial.',
      frames: [
        { title: 'Coffee run', pose: 'holding cup, casual glance', set: 'sidewalk café', top: 'oversized denim jacket', bottom: 'pleated mini skirt', extra: 'editorial polish' },
        { title: 'Crosswalk', pose: 'mid-stride crosswalk', set: 'city street, shallow depth', top: 'cropped knit', bottom: 'high-waisted jeans' },
        { title: 'Storefront', pose: 'leaning on storefront', set: 'glass reflections', top: 'blazer', bottom: 'tailored trousers' },
        { title: 'Alley look', pose: 'hands in pockets', set: 'moody alley, rim light', extra: 'minimal makeup' },
      ],
    },
    {
      key: 'fitness',
      title: 'Fitness Session',
      coverEmoji: '🏋️‍♀️',
      description: 'Warm-up → lift → stretch → cooldown.',
      frames: [
        { title: 'Warm-up', pose: 'dynamic stretch', set: 'gym floor', top: 'sports bra', bottom: 'compression leggings', extra: 'athletic tone' },
        { title: 'Lift', pose: 'deadlift setup', set: 'weight rack, gritty', extra: 'editorial polish' },
        { title: 'Stretch', pose: 'seated hamstring stretch', set: 'mat area', extra: 'minimal makeup' },
        { title: 'Cooldown', pose: 'hands behind head, relaxed', set: 'locker area' },
      ],
    },
  ];

  private backToList() {
    this.selected = undefined;
    this.editingIndex = null;
  }

  private editFrame(ix: number) {
    this.editingIndex = ix;
    // focus would be handled by prompt-input on expand
  }

  private stopEditing() {
    this.editingIndex = null;
  }

  private updateFrameValue(ix: number, key: keyof Frame, value: string) {
    if (!this.selected) return;
    const frames = [...this.selected.frames];
    const next = { ...frames[ix], [key]: value };
    frames[ix] = next;
    this.selected = { ...this.selected, frames };
  }

  private frameToPromptPayload(f: Frame): PromptPayload {
    const clothing = [f.top, f.bottom].filter(Boolean).join(', ');
    return {
      style: f.extra || '',
      clothing,
      pose: f.pose || '',
      set: f.set || '',
    };
  }

  private generateScenario() {
    if (!this.selected) return;
    for (const f of this.selected.frames) {
      const payload = this.frameToPromptPayload(f);
      this.dispatchEvent(new CustomEvent('enqueue-prompt', {
        detail: { prompt: payload },
        bubbles: true,
        composed: true,
      }));
    }
  }

  private pill(label: string, value?: string) {
    if (!value) return null;
    return html`
      <span class="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px]">
        <span class="text-[10px] uppercase tracking-wide text-slate-400">${label}</span>
        <span class="max-w-[14rem] truncate">${value}</span>
      </span>
    `;
  }

  render() {
    // Scenario gallery
    if (!this.selected) {
      return html`
        <section class="space-y-4">
          <header class="flex items-center justify-between">
            <h2 class="text-lg font-semibold">Pick a scenario</h2>
          </header>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            ${this.scenarios.map(s => html`
              <button
                class="text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 p-4"
                @click=${() => (this.selected = s)}
              >
                <div class="text-3xl">${s.coverEmoji ?? '🖼️'}</div>
                <div class="mt-2 text-base font-semibold">${s.title}</div>
                ${s.description ? html`<div class="text-sm text-slate-400 mt-1">${s.description}</div>` : null}
                <div class="mt-3 text-xs text-slate-400">${s.frames.length} frames</div>
              </button>
            `)}
          </div>
        </section>
      `;
    }

    // Selected scenario detail
    return html`
      <section class="space-y-4">
        <header class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <button
              class="rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-sm"
              @click=${() => this.backToList()}
            >← Scenarios</button>
            <h2 class="text-lg font-semibold">${this.selected.title}</h2>
          </div>

          <button
            class="inline-flex items-center gap-2 rounded-xl border border-indigo-400/30 bg-indigo-500/20 hover:bg-indigo-500/30 px-4 py-2.5 text-sm font-medium"
            @click=${() => this.generateScenario()}
            title="Queue each frame as a job"
          >
            Generate Scenario
          </button>
        </header>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          ${this.selected.frames.map((f, ix) => {
            const editing = this.editingIndex === ix;
            return html`
              <div class="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div class="flex items-center justify-between">
                  <div class="font-medium">${f.title || `Frame ${ix+1}`}</div>
                  ${!editing ? html`
                    <button
                      class="rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 px-2 py-1 text-xs"
                      @click=${() => this.editFrame(ix)}
                    >Edit</button>
                  ` : html`
                    <button
                      class="rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 px-2 py-1 text-xs"
                      @click=${() => this.stopEditing()}
                    >Done</button>
                  `}
                </div>

                ${!editing ? html`
                  <div class="mt-3 flex flex-wrap gap-1.5">
                    ${this.pill('Top', f.top)}
                    ${this.pill('Bottom', f.bottom)}
                    ${this.pill('Pose', f.pose)}
                    ${this.pill('Set', f.set)}
                    ${this.pill('Style', f.extra)}
                  </div>
                ` : html`
                  <div class="mt-3 space-y-3">
                    <prompt-input
                      .label=${'Top garment'}
                      .values=${[]}
                      allow-custom
                      .initialValue=${f.top || ''}
                      @change=${(e: CustomEvent) => this.updateFrameValue(ix, 'top', e.detail.value)}
                    ></prompt-input>
                    <prompt-input
                      .label=${'Bottom garment'}
                      .values=${[]}
                      allow-custom
                      .initialValue=${f.bottom || ''}
                      @change=${(e: CustomEvent) => this.updateFrameValue(ix, 'bottom', e.detail.value)}
                    ></prompt-input>
                    <prompt-input
                      .label=${'Pose'}
                      .values=${[]}
                      allow-custom
                      .initialValue=${f.pose || ''}
                      @change=${(e: CustomEvent) => this.updateFrameValue(ix, 'pose', e.detail.value)}
                    ></prompt-input>
                    <prompt-input
                      .label=${'Set / environment'}
                      .values=${[]}
                      allow-custom
                      .initialValue=${f.set || ''}
                      @change=${(e: CustomEvent) => this.updateFrameValue(ix, 'set', e.detail.value)}
                    ></prompt-input>
                    <prompt-input
                      .label=${'Extra (style / body)'}
                      .values=${[]}
                      allow-custom
                      .initialValue=${f.extra || ''}
                      @change=${(e: CustomEvent) => this.updateFrameValue(ix, 'extra', e.detail.value)}
                    ></prompt-input>
                  </div>
                `}
              </div>
            `;
          })}
        </div>
      </section>
    `;
  }
}

